// ICC v2 matrix/TRC display profile generator (§9) plus a minimal parser used by tests.
// Tags: desc, cprt, wtpt (D50), rXYZ/gXYZ/bXYZ (Bradford-adapted to D50), rTRC/gTRC/bTRC.
import { mul, TO_XYZ, type ColorSpaceId, type Mat3 } from './matrix';
import { bradford, D50_XYZ, D65_XYZ } from './planckian';
import { srgbToLinear, ADOBE_GAMMA } from './transfer';

export const PROFILE_NAMES: Record<ColorSpaceId, string> = {
  srgb: 'sRGB IEC61966-2.1',
  p3: 'Display P3',
  adobergb: 'Adobe RGB (1998) compatible',
};
const COPYRIGHT = 'No copyright, use freely';

/** RGB → XYZ(D50) colorant matrix for a space (D65 primaries Bradford-adapted to the PCS illuminant). */
export function d50Colorants(space: ColorSpaceId): Mat3 {
  return mul(bradford(D65_XYZ, D50_XYZ), TO_XYZ[space]);
}

const s15f16 = (v: number) => Math.round(v * 65536) | 0;

class Writer {
  bytes: number[] = [];
  u8(v: number) {
    this.bytes.push(v & 255);
  }
  u16(v: number) {
    this.u8(v >>> 8);
    this.u8(v);
  }
  u32(v: number) {
    this.u16(v >>> 16);
    this.u16(v & 0xffff);
  }
  sig(s: string) {
    for (let i = 0; i < 4; i++) this.u8(s.charCodeAt(i));
  }
  ascii(s: string) {
    for (const c of s) this.u8(c.charCodeAt(0));
  }
  pad4() {
    while (this.bytes.length % 4) this.u8(0);
  }
}

function descTag(text: string): number[] {
  // textDescriptionType (ICC v2): 'desc', reserved, ASCII count incl. NUL, ASCII, Unicode (0), ScriptCode (0).
  const w = new Writer();
  w.sig('desc');
  w.u32(0);
  w.u32(text.length + 1);
  w.ascii(text);
  w.u8(0);
  w.u32(0);
  w.u32(0); // Unicode language code, count
  w.u16(0);
  w.u8(0);
  for (let i = 0; i < 67; i++) w.u8(0); // ScriptCode code, count, 67 bytes
  return w.bytes;
}
function textTag(text: string): number[] {
  const w = new Writer();
  w.sig('text');
  w.u32(0);
  w.ascii(text);
  w.u8(0);
  return w.bytes;
}
function xyzTag(x: number, y: number, z: number): number[] {
  const w = new Writer();
  w.sig('XYZ ');
  w.u32(0);
  w.u32(s15f16(x));
  w.u32(s15f16(y));
  w.u32(s15f16(z));
  return w.bytes;
}
function curvTag(space: ColorSpaceId): number[] {
  const w = new Writer();
  w.sig('curv');
  w.u32(0);
  if (space === 'adobergb') {
    w.u32(1);
    w.u16(Math.round(ADOBE_GAMMA * 256)); // u8Fixed8: 563/256 is exact
  } else {
    w.u32(1024);
    for (let i = 0; i < 1024; i++) w.u16(Math.round(srgbToLinear(i / 1023) * 65535));
  }
  return w.bytes;
}

export function makeIccProfile(space: ColorSpaceId): Uint8Array {
  const m = d50Colorants(space);
  const curv = curvTag(space);
  const tags: { sig: string; data: number[] }[] = [
    { sig: 'desc', data: descTag(PROFILE_NAMES[space]) },
    { sig: 'cprt', data: textTag(COPYRIGHT) },
    { sig: 'wtpt', data: xyzTag(...D50_XYZ) },
    { sig: 'rXYZ', data: xyzTag(m[0][0], m[1][0], m[2][0]) },
    { sig: 'gXYZ', data: xyzTag(m[0][1], m[1][1], m[2][1]) },
    { sig: 'bXYZ', data: xyzTag(m[0][2], m[1][2], m[2][2]) },
    { sig: 'rTRC', data: curv },
    { sig: 'gTRC', data: curv },
    { sig: 'bTRC', data: curv },
  ];
  // Layout: header (128) + tag count (4) + table (12 each) + data. Shared TRC data is written once.
  const tableEnd = 128 + 4 + tags.length * 12;
  const offsets = new Map<number[], number>();
  const data = new Writer();
  for (const t of tags) {
    if (offsets.has(t.data)) continue;
    offsets.set(t.data, tableEnd + data.bytes.length);
    data.bytes.push(...t.data);
    data.pad4();
  }
  const size = tableEnd + data.bytes.length;
  const w = new Writer();
  w.u32(size);
  w.sig('none'); // preferred CMM: none
  w.u32(0x02100000); // version 2.1.0
  w.sig('mntr');
  w.sig('RGB ');
  w.sig('XYZ ');
  // Fixed creation date (deterministic output): 2026-10-10 00:00:00.
  for (const v of [2026, 10, 10, 0, 0, 0]) w.u16(v);
  w.sig('acsp');
  w.u32(0); // platform
  w.u32(0); // flags
  w.u32(0);
  w.u32(0); // device manufacturer, model
  w.u32(0);
  w.u32(0); // device attributes
  w.u32(0); // rendering intent: perceptual
  w.u32(s15f16(D50_XYZ[0]));
  w.u32(s15f16(D50_XYZ[1]));
  w.u32(s15f16(D50_XYZ[2]));
  w.sig('none'); // creator
  while (w.bytes.length < 128) w.u8(0); // profile ID (zero in v2) + reserved
  w.u32(tags.length);
  for (const t of tags) {
    w.sig(t.sig);
    w.u32(offsets.get(t.data)!);
    w.u32(t.data.length);
  }
  w.bytes.push(...data.bytes);
  return new Uint8Array(w.bytes);
}

// ── Parser (tests and sanity checks) ────────────────────────────────
export type ParsedIcc = {
  size: number;
  version: number;
  deviceClass: string;
  colorSpace: string;
  pcs: string;
  tags: Map<string, Uint8Array>;
  description: string;
};

const sigAt = (b: Uint8Array, o: number) => String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);

export function parseIcc(b: Uint8Array): ParsedIcc {
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const n = dv.getUint32(128);
  const tags = new Map<string, Uint8Array>();
  for (let i = 0; i < n; i++) {
    const o = 132 + i * 12;
    tags.set(sigAt(b, o), b.subarray(dv.getUint32(o + 4), dv.getUint32(o + 4) + dv.getUint32(o + 8)));
  }
  const desc = tags.get('desc');
  let description = '';
  if (desc && sigAt(desc, 0) === 'desc') {
    const len = new DataView(desc.buffer, desc.byteOffset).getUint32(8);
    description = String.fromCharCode(...desc.subarray(12, 12 + len - 1));
  }
  return {
    size: dv.getUint32(0),
    version: dv.getUint32(8),
    deviceClass: sigAt(b, 12),
    colorSpace: sigAt(b, 16),
    pcs: sigAt(b, 20),
    tags,
    description,
  };
}

export function readXyzTag(t: Uint8Array): [number, number, number] {
  const dv = new DataView(t.buffer, t.byteOffset, t.byteLength);
  return [dv.getInt32(8) / 65536, dv.getInt32(12) / 65536, dv.getInt32(16) / 65536];
}
